// @ts-ignore
import { deserialize } from 'deserialize-json-api';
import BasketApi from '../api/basket/BasketApi';
import basketStore from '../store/basket';
import { IAddItemToBasketDto, IBasket } from '../api/basket/BasketApi.i';
import { CustomerApi } from '../api/customer/CustomerApi';
import eventEmitter from '../eventEmitter/EventEmitter';
import eventTracker from '../eventTracker/EventTracker';

export class BasketService {
  public async initBasketService(): Promise<void> {
    this.basketApi = new BasketApi();
    this.customerApi = new CustomerApi();
    if (this.basketId) {
      try {
        await this.getBasket();
      } catch (err) {
        try {
          await this.createBasket();
        } catch (basketErr) {
          await this.createCustomer();
          this.createBasket();
        }
      }
    }
  }

  private basketApi!: BasketApi;

  private customerApi!: CustomerApi;

  // eslint-disable-next-line class-methods-use-this
  get basketId(): string {
    return basketStore.store.basketId;
  }

  // eslint-disable-next-line class-methods-use-this
  get customerId(): string {
    return basketStore.store.customerId;
  }

  async createCustomer(): Promise<void> {
    const customerResponse = await this.customerApi.createCustomer();
    const { data } = deserialize(customerResponse.data);
    basketStore.setCustomerId(data.id);
  }

  async createBasket(): Promise<IBasket | undefined> {
    if (basketStore.store.isBasketCreating) {
      return;
    }

    basketStore.setIsBasketCreating(true);

    if (!this.customerId) {
      await this.createCustomer();
    }
    const response = await this.basketApi.createBasket(this.customerId);
    const { data } = deserialize(response.data);
    basketStore.setBasketId(data.id);
    basketStore.setIsBasketCreating(false);

    return data;
  }

  async getBasket(): Promise<IBasket | undefined> {
    const response = await this.basketApi.getBasket(this.basketId, 'basket', ['basketItem', 'basketPromoCode', 'basketItem.product']);
    const { data } = deserialize(response.data);

    basketStore.setProducts(data.basketItem || []);
    basketStore.setTotal(Number(data.total));

    if (data.basketPromoCode?.code) {
      basketStore.setPromocode(data.basketPromoCode?.code);
    } else {
      basketStore.setPromocode('');
    }

    eventTracker.dispatchEvent('get-basket', data);

    return data;
  }

  async addToBasket(payload: IAddItemToBasketDto): Promise<IBasket | undefined> {
    if (basketStore.store.isBasketCreating) {
      return;
    }

    if (!this.basketId) {
      await this.createBasket();
    }
    const response = await this.basketApi.addToBasket(this.basketId, payload);
    const { data } = deserialize(response.data);
    this.getBasket();
    return data;
  }

  async changeQuantity(
    itemId: string,
    quantity: number,
  ): Promise<IBasket | undefined> {
    const response = await this.basketApi.changeQuantity(this.basketId, itemId, quantity);
    const { data } = deserialize(response.data);
    this.getBasket();
    return data;
  }

  async deleteProduct(itemId: string): Promise<{ data: IBasket } | undefined> {
    const response = await this.basketApi.deleteProduct(this.basketId, itemId);
    const { data } = deserialize(response.data);
    this.getBasket();
    return data;
  }

  async applyPromoCode(promocode: string): Promise<any> {
    if (basketStore.store.isBasketCreating) {
      return;
    }

    if (!this.basketId) {
      await this.createBasket();
    }

    const response = await this.basketApi.applyPromoCode(this.basketId, promocode);
    const { data } = deserialize(response.data);
    this.getBasket();
    eventEmitter.emit('promocode-changed', {});

    return data;
  }

  async removePromoCode(promocode: string): Promise<any> {
    if (basketStore.store.isBasketCreating) {
      return;
    }

    if (!this.basketId) {
      await this.createBasket();
    }

    const response = await this.basketApi.removePromoCode(this.basketId, promocode);
    const { data } = deserialize(response.data);
    this.getBasket();
    eventEmitter.emit('promocode-changed', {});

    return data;
  }
}

export default new BasketService();
